/* eslint-disable sort-keys-fix/sort-keys-fix */
import React, { useState } from 'react';
import * as R from 'ramda';
import { arrayOf, func, shape, string } from 'prop-types';
import { styled, useTheme } from '@mui/material/styles';
import { formatVolume } from 'utils/helpers/format';

// Components
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';

import NoAuthorizedCarriers from './NoAuthorizedCarriers';

const PerformanceGridItem = styled(Typography)(({ theme }) => ({
  '&:first-child': {
    flex: '0 1 280px',
    marginLeft: 0,
  },
  '&:nth-child(2)': {
    flex: '0 1 125px',
  },
  // eslint-disable-next-line
    '&:last-child': {
    flex: '0 1 179px',
  },
  [theme.breakpoints.down(1100)]: {
    '&:first-child': {
      flex: '0 1 130px',
    },
  },
  [theme.breakpoints.down('lg')]: {
    '&:nth-child(2)': {
      flex: '0 1 100px',
    },
    // eslint-disable-next-line
      '&:last-child': {
      flex: '0 1 100px',
    },
    margin: theme.spacing(0, 0, 2),
  },
  [theme.breakpoints.down('md')]: {
    '&:first-child': {
      flex: '0 1 280px',
    },
  },
  margin: theme.spacing(0, 2, 1),
  whiteSpace: 'nowrap',
}));

const tooltipMessage = (
  <>
    <p>
      Carrier Performance is based on prior 90 day loading activity at the selected Magellan
      Terminal.
    </p>
    <p>
      The Performance Metric is determined by the difference in Carrier&apos;s Average time to Load
      (Card in – BOL Print) vs the Terminals average loading time over the past 90 days.
    </p>
    <p>
      The Barrels Loaded is the total amount of product that has been loaded by the Carrier for the
      selected Affiliate over the past 90 days.
    </p>
  </>
);

const CarrierPeformance = ({
  carrierPerformance,
  carrierSortColumn,
  carrierSortDirection,
  setCarrierSortColumn,
  setCarrierSortDirection,
}) => {
  const theme = useTheme();

  const mdUp = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });
  const rows = carrierPerformance.length >= 5 && mdUp ? 5 : carrierPerformance.length;

  const [isTableCollapsed, setIsTableCollapsed] = useState(true);
  const [rowsToShow, setRowsToShow] = useState(rows);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const hasCarrierPerformance = carrierPerformance.length;

  const buttonLabel = () => (isTableCollapsed ? 'View More' : 'Collapse View');

  const getChipColor = value =>
    R.cond([
      [R.lt(0), R.always('secondary.light')],
      [R.or(R.equals(0.0), R.equals(0)), R.always('secondary.medium')],
      [R.gt(0), R.always('primary.dark')],
      [R.isNil, R.always('surface.white')],
    ])(value);

  const getChipTextColor = value =>
    R.cond([
      [R.gt(0), R.always('surface.white')],
      [R.T, R.always('primary.dark')],
    ])(value);

  const renderChipLabel = val => {
    if (val) return val.toFixed(1) > 0 ? `+${val.toFixed(1)}` : val.toFixed(1);
    return '--';
  };

  const handleClick = () => {
    setIsTableCollapsed(!isTableCollapsed);
    if (isTableCollapsed) {
      setRowsToShow(carrierPerformance.length);
    } else {
      setRowsToShow(5);
    }
  };

  const renderCarrierPerformance = () => {
    const data = [];
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < rowsToShow; i++) {
      data.push(
        <Box
          key={carrierPerformance[i].carrierId}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mb: { xs: 0, lg: 1 },
            mt: { xs: 0, lg: 2 },
            mx: 0,
          }}
        >
          <PerformanceGridItem
            align="left"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            variant="subtitle2"
          >
            {carrierPerformance[i].carrierName}
          </PerformanceGridItem>
          <PerformanceGridItem
            sx={{ display: 'flex', justifyContent: mdUp ? 'center' : 'flex-end' }}
          >
            <Chip
              label={renderChipLabel(carrierPerformance[i].performanceScore)}
              sx={{
                '& > span': {
                  ...theme.typography.caption,
                  color: getChipTextColor(carrierPerformance[i].performanceScore),
                },
                backgroundColor: getChipColor(carrierPerformance[i].performanceScore),
                height: 21,
                p: '5px 0',
              }}
            />
          </PerformanceGridItem>
          <PerformanceGridItem align={mdUp ? 'center' : 'right'} variant="subtitle2">
            {formatVolume(carrierPerformance[i].totalLoadedBBLs)}
          </PerformanceGridItem>
        </Box>
      );
    }
    return data;
  };

  const renderSort = str => {
    if (str === carrierSortColumn) {
      return carrierSortDirection === 'dsc' ? (
        <ArrowDownwardIcon
          sx={{
            color: 'primary.dark',
            height: 18,
            position: 'absolute',
            right: -18,
            top: 0,
            width: 18,
          }}
        />
      ) : (
        <ArrowUpwardIcon
          sx={{
            color: 'primary.dark',
            height: 18,
            position: 'absolute',
            right: -18,
            top: 0,
            width: 18,
          }}
        />
      );
    }
    return null;
  };

  const handleOnClick = col => {
    carrierSortColumn !== col && setCarrierSortColumn(col);
    if (carrierSortColumn === col) {
      carrierSortDirection === 'asc'
        ? setCarrierSortDirection('dsc')
        : setCarrierSortDirection('asc');
    } else {
      setCarrierSortDirection('dsc');
    }
  };

  return (
    <>
      {!!hasCarrierPerformance && (
        <Box
          sx={{
            alignItems: { xs: 'center', md: 'revert' },
            backgroundColor: 'surface.white',
            borderBottom: { xs: `1px solid ${theme.palette.graphic.light}`, md: 'none' },
            display: { xs: 'flex', md: 'revert' },
            height: { xs: 56, md: 'revert' },
            justifyContent: 'space-between',
            mb: { xs: 2, md: 'revert' },
            pb: 2,
            pl: { xs: 2.5, md: 0 },
            pr: { xs: 2, md: 0 },
            pt: 2,
          }}
        >
          <Typography variant={mdUp ? 'h5' : 'subtitle2'}>Carrier Performance</Typography>
          {mdUp ? (
            <Tooltip enterTouchDelay={10} placement="top" title={tooltipMessage}>
              <Typography
                sx={{
                  color: 'secondary.main',
                  cursor: 'pointer',
                  textTransform: 'unset',
                }}
                variant="button"
              >
                How is this calculated?
              </Typography>
            </Tooltip>
          ) : (
            <Button onClick={() => setIsModalOpen(true)} sx={{ padding: 0 }} variant="text">
              View Data
            </Button>
          )}
        </Box>
      )}
      <Box
        sx={{
          display: { xs: 'none', md: 'revert' },
          pb: '22px',
          transition: theme.transitions.create('height', {
            duration: theme.transitions.duration.leavingScreen,
            easing: theme.transitions.easing.sharp,
          }),
          width: '100%',
        }}
      >
        {hasCarrierPerformance ? (
          <>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mb: { xs: 0, lg: 1 },
                mt: { xs: 0, lg: 2 },
                mx: 0,
              }}
            >
              <PerformanceGridItem>
                <ButtonBase
                  onClick={() => handleOnClick('carrierName')}
                  sx={{
                    ...theme.typography.subtitle2,
                    position: 'relative',
                  }}
                >
                  CARRIER
                  {renderSort('carrierName')}
                </ButtonBase>
              </PerformanceGridItem>
              <PerformanceGridItem sx={{ textAlign: 'center' }}>
                <ButtonBase
                  onClick={() => handleOnClick('performanceScore')}
                  sx={{
                    ...theme.typography.subtitle2,
                    position: 'relative',
                  }}
                >
                  PERFORMANCE
                  {renderSort('performanceScore')}
                </ButtonBase>
              </PerformanceGridItem>
              <PerformanceGridItem sx={{ textAlign: 'center' }}>
                <ButtonBase
                  onClick={() => handleOnClick('totalLoadedBBLs')}
                  sx={{
                    ...theme.typography.subtitle2,
                    position: 'relative',
                  }}
                >
                  BARRELS LOADED
                  {renderSort('totalLoadedBBLs')}
                </ButtonBase>
              </PerformanceGridItem>
            </Box>
            {renderCarrierPerformance()}
            {carrierPerformance.length > 5 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Button onClick={() => handleClick()} variant="text">
                  {buttonLabel()}
                </Button>
              </Box>
            )}
          </>
        ) : (
          <NoAuthorizedCarriers />
        )}
      </Box>
      <Dialog
        aria-labelledby="customized-dialog-title"
        onClose={() => setIsModalOpen(false)}
        open={isModalOpen}
        slotProps={{
          paper: {
            className: Dialog,
          },
        }}
        sx={{ display: { xs: 'revert', md: 'none' }, minWidth: 350 }}
      >
        <DialogTitle
          sx={{
            '& > h2': {
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
            },
            padding: 2,
          }}
        >
          Carrier Performance
          <IconButton
            aria-label="close"
            onClick={() => setIsModalOpen(false)}
            size="large"
            sx={{ color: 'gray.main', padding: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>{renderCarrierPerformance()}</DialogContent>
      </Dialog>
    </>
  );
};

CarrierPeformance.propTypes = {
  carrierPerformance: arrayOf(shape({})).isRequired,
  carrierSortColumn: string.isRequired,
  carrierSortDirection: string.isRequired,
  setCarrierSortColumn: func.isRequired,
  setCarrierSortDirection: func.isRequired,
};

export default CarrierPeformance;
